import React, { useContext, useEffect, useState } from 'react';
import { FaPlus, FaPen, FaTrash, FaArrowsAltV, FaRegDotCircle } from 'react-icons/fa';
import AppContext from '../../contexts/AppContext';

const ACTION_STYLES = {
    create: { icon: FaPlus, bg: '#e7f7ec', ring: 'rgba(15,139,109,0.16)', text: '#0f8b6d' },
    add: { icon: FaPlus, bg: '#e7f7ec', ring: 'rgba(15,139,109,0.16)', text: '#0f8b6d' },
    update: { icon: FaPen, bg: '#eef1ff', ring: 'rgba(74,92,255,0.16)', text: '#4a5cff' },
    edit: { icon: FaPen, bg: '#eef1ff', ring: 'rgba(74,92,255,0.16)', text: '#4a5cff' },
    delete: { icon: FaTrash, bg: '#fdecea', ring: 'rgba(217,102,91,0.16)', text: '#c1584d' },
    remove: { icon: FaTrash, bg: '#fdecea', ring: 'rgba(217,102,91,0.16)', text: '#c1584d' },
    reorder: { icon: FaArrowsAltV, bg: '#fff6e0', ring: 'rgba(184,134,11,0.16)', text: '#b8860b' },
};
const DEFAULT_ACTION_STYLE = { icon: FaRegDotCircle, bg: '#f1f3f8', ring: 'rgba(120,130,150,0.16)', text: '#8a97b4' };

const formatDateTime = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const formatRelativeTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const diffMs = Date.now() - date.getTime();
    const diffMinutes = Math.round(diffMs / 60000);
    if (diffMinutes < 1) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.round(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    return formatDateTime(value);
};

const RecentChangesDashboard = () => {
    const { token, selectedRole } = useContext(AppContext);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let ignore = false;

        const fetchRecentChanges = async () => {
            if (!['venue', 'tsVenue'].includes(selectedRole?.type) || !selectedRole?.id || !token) {
                if (!ignore) {
                    setLogs([]);
                    setError('');
                    setLoading(false);
                }
                return;
            }

            const endpoint = selectedRole.type === 'tsVenue'
                ? `${process.env.REACT_APP_API_BASE_URL}/private/audit-logs/tsvenue/${selectedRole.id}`
                : `${process.env.REACT_APP_API_BASE_URL}/private/audit-logs/${selectedRole.id}`;

            try {
                if (!ignore) {
                    setLoading(true);
                    setError('');
                }
                const response = await fetch(
                    endpoint,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                 if (!response.ok) {
                     const result = await response.json().catch(() => ({}));
                     throw new Error(result?.error || 'Failed to load recent changes');
                 }

                const data = await response.json();
                if (!ignore) {
                    setLogs(Array.isArray(data) ? data.slice(0, 5) : []);
                    setError('');
                }
            } catch (error) {
                console.error('Error fetching recent changes:', error);
                if (!ignore) {
                    setLogs([]);
                    setError(error instanceof Error ? error.message : 'Failed to load recent changes');
                }
            } finally {
                if (!ignore) {
                    setLoading(false);
                }
            }
        };

        fetchRecentChanges();
        return () => {
            ignore = true;
        };
    }, [selectedRole?.id, selectedRole?.type, token]);

    return (
        <div className="relative flex flex-col gap-4 rounded-[24px] border border-[#e7ebf8] bg-[linear-gradient(180deg,#ffffff_0%,#fbfcff_100%)] px-6 pb-6 pt-5 shadow-[0_16px_34px_rgba(30,52,120,0.06)]">
            <div>
                <h3 className="mb-1 text-[1.1rem] font-semibold tracking-[-0.02em] text-[#1f2d4d]">Recent Changes</h3>
                <p className="text-sm text-[#6b7894]">Latest CMS edits made to this venue.</p>
            </div>

            {loading ? (
                <div className="rounded-[18px] border border-[#e6ebf8] bg-[#fbfcff] px-4 py-4 text-sm text-[#7a88a6]">Loading recent changes...</div>
            ) : error ? (
                <div className="rounded-[18px] border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">Failed to load recent changes: {error}</div>
            ) : logs.length === 0 ? (
                <div className="rounded-[18px] border border-[#e6ebf8] bg-[#fbfcff] px-4 py-4 text-sm text-[#7a88a6]">No recent changes available.</div>
            ) : (
                <div className="flex flex-col gap-3 pt-1">
                    {logs.map((log) => {
                        const changedFields = (() => {
                            if (log?.action !== 'update') {
                                return [];
                            }

                            if (Array.isArray(log?.meta?.changed_fields)) {
                                return log.meta.changed_fields
                                    .map((item) => (typeof item === 'string' ? item : item?.field))
                                    .filter(Boolean);
                            }

                            if (Array.isArray(log?.meta?.fields)) {
                                return log.meta.fields.filter((field) => typeof field === 'string' && field);
                            }

                            return [];
                        })();
                        const style = ACTION_STYLES[log?.action] || DEFAULT_ACTION_STYLE;
                        const Icon = style.icon;

                        return (
                            <div key={log.id} className="flex items-start gap-3 rounded-[16px] border border-[#e6ebf8] bg-white px-4 py-3 shadow-[0_10px_20px_rgba(15,23,42,0.03)]">
                                <span
                                    className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full"
                                    style={{ backgroundColor: style.bg, boxShadow: `0 0 0 4px ${style.ring}`, color: style.text }}
                                >
                                    <Icon size={13} />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                                        <span className="text-sm font-semibold text-[#1f2d4d]">
                                            {log.summary || 'No summary available'}
                                        </span>
                                        <span className="flex-none text-xs text-[#8a97b4]" title={formatDateTime(log.createdAt)}>
                                            {formatRelativeTime(log.createdAt)}
                                        </span>
                                    </div>
                                    {changedFields.length > 0 && (
                                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                                            {changedFields.map((field) => (
                                                <span key={field} className="rounded-full bg-[#eef1f8] px-2 py-0.5 text-[0.7rem] font-medium text-[#5b6b8c]">
                                                    {field}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mt-1.5 text-xs text-[#8a97b4]">
                                        {log.actor_name || log.actor_email || 'Unknown user'}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default RecentChangesDashboard;
